import { useCallback, useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { AnalyticsHubDetailSnapshot } from '@/types/analytics';
import { formatCompactNumber } from '@/utils/formatNumber';
import { useTimezone } from '@/contexts/TimezoneContext';
import { isMetricSupported } from '@/constants/platformAnalytics';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface SnapshotChartProps {
  snapshots: AnalyticsHubDetailSnapshot[];
  publishedAt: string;
  platform?: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: AnalyticsHubDetailSnapshot;
  }>;
  formatTooltipDate: (dateStr: string) => string;
  platform?: string;
}

/**
 * Custom tooltip showing all metrics for a snapshot
 */
const CustomTooltip = ({ active, payload, formatTooltipDate, platform }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null;

  const data = payload[0]?.payload as AnalyticsHubDetailSnapshot;

  // Show metrics based on platform support (or all if platform not specified)
  const showReach = !platform || isMetricSupported(platform, 'reach');
  const showImpressions = !platform || isMetricSupported(platform, 'impressions');
  const showVideoViews = !platform || isMetricSupported(platform, 'videoViews');
  const showShares = !platform || isMetricSupported(platform, 'shares');

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-2.5 sm:p-3 text-xs sm:text-sm">
      <p className="font-medium text-gray-900 mb-1.5 sm:mb-2">{formatTooltipDate(data.fetchedAt)}</p>
      <div className="space-y-0.5 sm:space-y-1">
        {showReach && (
          <p>
            <span className="text-gray-500">Reach:</span> {formatCompactNumber(data.reach)}
          </p>
        )}
        {showImpressions && (
          <p>
            <span className="text-gray-500">Impressions:</span>{' '}
            {formatCompactNumber(data.impressions)}
          </p>
        )}
        {showVideoViews && data.videoViews != null && data.videoViews > 0 && (
          <p>
            <span className="text-gray-500">Video Views:</span>{' '}
            {formatCompactNumber(data.videoViews)}
          </p>
        )}
        <p>
          <span className="text-gray-500">Engagement:</span>{' '}
          {formatCompactNumber(data.engagement)}
        </p>
        <p>
          <span className="text-gray-500">Likes:</span> {data.likes.toLocaleString()}
        </p>
        <p>
          <span className="text-gray-500">Comments:</span> {data.comments.toLocaleString()}
        </p>
        {showShares && (
          <p>
            <span className="text-gray-500">Shares:</span> {data.shares.toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * SnapshotChart Component
 *
 * Displays analytics performance over time with engagement and reach lines
 * Platform-aware: only shows metrics that the platform supports
 */
export const SnapshotChart = ({ snapshots, publishedAt, platform }: SnapshotChartProps) => {
  const { formatDate, formatDateTime } = useTimezone();
  const isMobile = useMediaQuery('(max-width: 767px)');

  // Determine which metrics to show based on platform support
  const showReach = !platform || isMetricSupported(platform, 'reach');
  // Fall back to videoViews as the distribution line when reach is not supported (e.g. TikTok)
  const showVideoViews = !showReach && (!platform || isMetricSupported(platform, 'videoViews'));

  // Memoized formatter for chart dates (timezone-aware)
  // For X-axis ticks: short format (dd.MM)
  const formatChartDate = useCallback(
    (dateStr: string): string => {
      return formatDate(dateStr, 'dayMonth');
    },
    [formatDate]
  );

  // For tooltip: full date + time (e.g., "Jan 15, 2025, 2:30 PM")
  const formatTooltipDate = useCallback(
    (dateStr: string): string => {
      return formatDateTime(dateStr);
    },
    [formatDateTime]
  );

  // Build chart data: start with publish moment (zero metrics), then actual snapshots
  const chartData = useMemo(() => {
    const zeroPoint: AnalyticsHubDetailSnapshot = {
      snapshotNumber: 0,
      fetchedAt: publishedAt,
      reach: 0,
      impressions: 0,
      engagement: 0,
      likes: 0,
      comments: 0,
      shares: 0,
      saves: 0,
      clicks: 0,
      videoViews: null,
      videoWatchTime: null,
      videoCompletionRate: null,
    };

    // Filter out any snapshots that are before publishedAt
    const publishTime = new Date(publishedAt).getTime();
    const validSnapshots = snapshots.filter(
      (s) => new Date(s.fetchedAt).getTime() >= publishTime
    );

    return [zeroPoint, ...validSnapshots];
  }, [snapshots, publishedAt]);

  // Show empty state only if no snapshots at all
  if (snapshots.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400 text-sm">
        <i className="hm hm-graph text-2xl mb-2 block" />
        <p>Chart will appear after more snapshots are collected</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={isMobile ? 240 : 300}>
      <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
        <XAxis
          dataKey="fetchedAt"
          tickFormatter={formatChartDate}
          tick={{ fontSize: isMobile ? 10 : 12 }}
          tickMargin={6}
        />
        <YAxis
          tick={{ fontSize: isMobile ? 10 : 12 }}
          tickFormatter={(v) => formatCompactNumber(v)}
          width={isMobile ? 32 : 50}
        />
        <Tooltip content={<CustomTooltip formatTooltipDate={formatTooltipDate} platform={platform} />} />
        <Legend />
        <Line
          type="monotone"
          dataKey="engagement"
          stroke="#69C7C1"
          strokeWidth={2}
          dot={{ r: 4 }}
          name="Engagement"
        />
        {showReach && (
          <Line
            type="monotone"
            dataKey="reach"
            stroke="#074F47"
            strokeWidth={2}
            dot={{ r: 4 }}
            name="Reach"
          />
        )}
        {showVideoViews && (
          <Line
            type="monotone"
            dataKey="videoViews"
            stroke="#074F47"
            strokeWidth={2}
            dot={{ r: 4 }}
            name="Video Views"
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};
